import React, { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from 'react-i18next';
import { collection, addDoc } from "firebase/firestore";
import db from "../firebase";
import { FaEnvelope, FaUser, FaPaperPlane, FaCheckCircle, FaExclamationCircle } from "react-icons/fa";

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    service: "",
    message: "",
  });
  const [status, setStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: 'loading', message: t('contact.sending') });

    try {
      await addDoc(collection(db, "messages"), {
        ...formData,
        createdAt: new Date(),
      });
      setStatus({ type: 'success', message: t('contact.success') });
      setFormData({ name: "", email: "", service: "", message: "" });
    } catch (error) {
      console.error("Error al enviar el mensaje:", error);
      setStatus({ type: 'error', message: t('contact.error') });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      style={{
        background: 'linear-gradient(135deg, #1a1a1a 0%, #000000 100%)',
        padding: '80px 20px',
        position: 'relative'
      }}
    >
      <div className="container" style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-5"
        >
          <div
            style={{
              background: '#cc0033',
              color: 'white',
              padding: '8px 20px',
              fontSize: '0.85rem',
              fontWeight: '600',
              letterSpacing: '2px',
              textTransform: 'uppercase',
              marginBottom: '30px',
              display: 'inline-block'
            }}
          >
            {t('contact.tag')}
          </div>

          <h2
            style={{
              fontSize: '2.5rem',
              fontWeight: '700',
              color: 'white',
              marginBottom: '20px',
              fontFamily: 'Montserrat, sans-serif'
            }}
          >
            {t('contact.title')}
          </h2>

          <p
            style={{
              fontSize: '1.05rem',
              color: '#ccc',
              maxWidth: '700px',
              margin: '0 auto'
            }}
          >
            {t('contact.subtitle')}
          </p>
        </motion.div>

        {/* Formulario */}
        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            padding: '40px',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}
        >
          {/* Nombre */}
          <div className="mb-4">
            <label
              htmlFor="name"
              style={{
                color: 'white',
                fontSize: '0.95rem',
                fontWeight: '600',
                marginBottom: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <FaUser style={{ color: '#cc0033' }} />
              {t('contact.name')}
            </label>
            <input
              type="text"
              id="name"
              name="name"
              placeholder={t('contact.namePlaceholder')}
              value={formData.name}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '14px 16px',
                fontSize: '1rem',
                border: '2px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                outline: 'none',
                transition: 'all 0.3s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#cc0033';
                e.target.style.background = 'rgba(255, 255, 255, 0.08)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                e.target.style.background = 'rgba(255, 255, 255, 0.05)';
              }}
            />
          </div>

          {/* Email */}
          <div className="mb-4">
            <label
              htmlFor="email"
              style={{
                color: 'white',
                fontSize: '0.95rem',
                fontWeight: '600',
                marginBottom: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <FaEnvelope style={{ color: '#cc0033' }} />
              {t('contact.email')}
            </label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder={t('contact.emailPlaceholder')}
              value={formData.email}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '14px 16px',
                fontSize: '1rem',
                border: '2px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                outline: 'none',
                transition: 'all 0.3s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#cc0033';
                e.target.style.background = 'rgba(255, 255, 255, 0.08)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                e.target.style.background = 'rgba(255, 255, 255, 0.05)';
              }}
            />
          </div>

          {/* Servicio */}
          <div className="mb-4">
            <label
              htmlFor="service"
              style={{
                color: 'white',
                fontSize: '0.95rem',
                fontWeight: '600',
                marginBottom: '10px',
                display: 'block'
              }}
            >
              {t('contact.service')}
            </label>
            <select
              id="service"
              name="service"
              value={formData.service}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '14px 16px',
                fontSize: '1rem',
                border: '2px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                outline: 'none',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#cc0033';
                e.target.style.background = 'rgba(255, 255, 255, 0.08)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                e.target.style.background = 'rgba(255, 255, 255, 0.05)';
              }}
            >
              <option value="" style={{ background: '#1a1a1a', color: 'white' }}>
                {t('contact.servicePlaceholder')}
              </option>
              <option value="web" style={{ background: '#1a1a1a', color: 'white' }}>
                {t('contact.services.webDev')}
              </option>
              <option value="mobile" style={{ background: '#1a1a1a', color: 'white' }}>
                {t('contact.services.mobile')}
              </option>
              <option value="database" style={{ background: '#1a1a1a', color: 'white' }}>
                {t('contact.services.database')}
              </option>
              <option value="cloud" style={{ background: '#1a1a1a', color: 'white' }}>
                {t('contact.services.cloud')}
              </option>
              <option value="automation" style={{ background: '#1a1a1a', color: 'white' }}>
                {t('contact.services.automation')}
              </option>
            </select>
          </div>

          {/* Mensaje */}
          <div className="mb-4">
            <label
              htmlFor="message"
              style={{
                color: 'white',
                fontSize: '0.95rem',
                fontWeight: '600',
                marginBottom: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <FaPaperPlane style={{ color: '#cc0033' }} />
              {t('contact.message')}
            </label>
            <textarea
              id="message"
              name="message"
              rows="5"
              placeholder={t('contact.messagePlaceholder')}
              value={formData.message}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '14px 16px',
                fontSize: '1rem',
                border: '2px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                outline: 'none',
                transition: 'all 0.3s ease',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#cc0033';
                e.target.style.background = 'rgba(255, 255, 255, 0.08)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                e.target.style.background = 'rgba(255, 255, 255, 0.05)';
              }}
            />
          </div>

          {/* Botón de envío */}
          <motion.button
            type="submit"
            disabled={isSubmitting}
            whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
            whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
            style={{
              width: '100%',
              padding: '16px',
              fontSize: '1rem',
              fontWeight: '600',
              border: 'none',
              borderRadius: '8px',
              background: isSubmitting
                ? '#666'
                : 'linear-gradient(135deg, #cc0033 0%, #a60029 100%)',
              color: 'white',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              boxShadow: isSubmitting
                ? 'none'
                : '0 4px 15px rgba(204, 0, 51, 0.3)'
            }}
          >
            <FaPaperPlane />
            {isSubmitting ? t('contact.sending') : t('contact.submit')}
          </motion.button>

          {/* Mensaje de estado */}
          {status && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              style={{
                marginTop: '20px',
                padding: '16px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                background: `rgba(${status.type === 'success'
                    ? '40,167,69'
                    : status.type === 'error'
                      ? '220,53,69'
                      : '255,193,7'
                  }, 0.15)`,
                border: `1px solid rgba(${status.type === 'success'
                    ? '40,167,69'
                    : status.type === 'error'
                      ? '220,53,69'
                      : '255,193,7'
                  }, 0.3)`,
                color: status.type === 'success'
                  ? '#28a745'
                  : status.type === 'error'
                    ? '#dc3545'
                    : '#ffc107'
              }}
            >
              {status.type === 'success' && <FaCheckCircle size={20} />}
              {status.type === 'error' && <FaExclamationCircle size={20} />}
              <span style={{ fontSize: '0.95rem' }}>{status.message}</span>
            </motion.div>
          )}
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;
